/**
 * Conversión de unidades de stock — UNA sola regla para toda la app.
 *
 * El caso que obligó a escribirlo: se fotografía un bote de 250 ml de un producto
 * que se lleva en litros. El escáner lee bien la etiqueta («quantity: 250,
 * unit: "ml"»), pero la pantalla que recibía ese dato se quedaba con el 250 y
 * tiraba el «ml», así que 250 ml entraban como 250 L. Mil veces de más, sin que
 * nada chirriara por el camino.
 *
 * Había ya dos conversiones distintas —una en el alta de producto y otra en el
 * escáner— y una tercera pantalla sin ninguna. Aquí está la única.
 *
 * Dos decisiones a propósito:
 *
 * - **Sin unidad no se inventa nada**: si falta el origen o el destino se
 *   devuelve la cantidad tal cual (es lo que hacía la app antes de esto). Lo que
 *   no puede pasar es tirar una unidad que SÍ venía.
 * - **Entre familias distintas se devuelve `null`, no un número**: 5 kg no son
 *   5 L ni 5000 de nada. Quien llame decide qué hacer, pero nunca recibirá una
 *   cifra inventada.
 */

/** Unidades de stock válidas en inventario. */
export type StockUnit = "L" | "ml" | "kg" | "g" | "units";

type Family = "volume" | "mass" | "count";

/** Alias → unidad canónica. Cubre el «mL» de las etiquetas y el «ml» del stock. */
const ALIASES: Record<string, StockUnit> = {
  l: "L", lt: "L", ltr: "L", liter: "L", liters: "L", litro: "L", litros: "L", litri: "L",
  ml: "ml", mls: "ml", cc: "ml", milliliter: "ml", milliliters: "ml", mililitro: "ml", mililitros: "ml",
  kg: "kg", kgs: "kg", kilo: "kg", kilos: "kg", kilogram: "kg", kilograms: "kg", kilogramo: "kg", kilogramos: "kg",
  g: "g", gr: "g", grs: "g", gram: "g", grams: "g", gramo: "g", gramos: "g",
  units: "units", unit: "units", un: "units", ud: "units", uds: "units",
  unidad: "units", unidades: "units", pcs: "units", pzs: "units", pkg: "units", paquetes: "units",
};

const FAMILY: Record<StockUnit, Family> = {
  L: "volume", ml: "volume",
  kg: "mass", g: "mass",
  units: "count",
};

/** Cuánto vale una unidad en la base de su familia (ml para volumen, g para masa). */
const IN_BASE: Record<StockUnit, number> = { L: 1000, ml: 1, kg: 1000, g: 1, units: 1 };

/** Texto suelto («ML», «Litros», «  kg ») → unidad canónica, o null si no se reconoce. */
export function normalizeUnit(raw: string | null | undefined): StockUnit | null {
  if (!raw) return null;
  const key = String(raw).trim().toLowerCase();
  return ALIASES[key] ?? null;
}

/** ¿Se pueden convertir la una en la otra? Unidades desconocidas: no. */
export function sameFamily(a: string | null | undefined, b: string | null | undefined): boolean {
  const from = normalizeUnit(a);
  const to = normalizeUnit(b);
  if (!from || !to) return false;
  return FAMILY[from] === FAMILY[to];
}

/* 250 / 1000 = 0.25 exacto, pero 1/3 de litro no lo es: se recorta el ruido de
   coma flotante sin tocar cifras que el usuario pueda notar. */
const round6 = (n: number) => Math.round(n * 1e6) / 1e6;

/**
 * Pasa `qty` de `from` a `to`.
 *
 * - Unidades iguales, o alguna ausente/desconocida → `qty` sin tocar.
 * - Misma familia → el número convertido (250 ml → 0.25 L).
 * - Familias distintas (kg ↔ L, cualquiera ↔ units) → `null`.
 */
export function convertQuantity(
  qty: number,
  from: string | null | undefined,
  to: string | null | undefined,
): number | null {
  if (!isFinite(qty)) return null;
  const f = normalizeUnit(from);
  const t = normalizeUnit(to);
  // Sin unidad fiable en alguno de los dos lados no hay nada que convertir: se
  // devuelve tal cual, que es como se ha comportado la app siempre.
  if (!f || !t) return qty;
  if (f === t) return qty;
  if (FAMILY[f] !== FAMILY[t]) return null;
  return round6((qty * IN_BASE[f]) / IN_BASE[t]);
}

/**
 * Igual que `convertQuantity` pero contando además si hubo cambio de unidad, para
 * poder enseñarlo («1 × 250 mL = 0,25 L»). Nunca se convierte a espaldas del
 * usuario: si el número que ve no es el que leyó en la etiqueta, tiene que saber
 * por qué.
 */
export function describeConversion(
  qty: number,
  from: string | null | undefined,
  to: string | null | undefined,
): { value: number; converted: boolean; incompatible: boolean } {
  const result = convertQuantity(qty, from, to);
  if (result === null) return { value: qty, converted: false, incompatible: true };
  const f = normalizeUnit(from);
  const t = normalizeUnit(to);
  return { value: result, converted: !!f && !!t && f !== t, incompatible: false };
}

/**
 * ENVASES → LITROS O KILOS. La conversión que las otras no pueden hacer.
 *
 * Una factura de distribuidor no dice «10 L»: dice **2 UD** de una garrafa de
 * 5 L. Eso es la familia «count» contra la familia «volume», así que
 * `convertQuantity` devuelve `null` —y hace bien, porque 2 unidades de algo
 * desconocido no son 2 de nada—. Pero cuando el producto que YA tienes lleva
 * apuntado el tamaño de su envase, sí se sabe: 2 × 5 L = 10 L.
 *
 * Se usa ESE dato y no el de la factura a propósito: el tamaño del envase del
 * producto es un dato tuyo, comprobado al darlo de alta; lo que la IA leyó en
 * una línea de factura es una lectura. Si el producto no lo tiene apuntado, se
 * devuelve `null` y se pregunta — nunca se supone un tamaño.
 *
 * Solo va en un sentido (contar → medir). Al revés —10 L en un producto que se
 * lleva por unidades— haría falta que la división cuadrase exacta y no es un
 * caso que aparezca en las facturas.
 */
export interface ContainerSpec {
  /** `packaging_unit_content` del producto: cuánto trae UN envase. */
  content?: number | null;
  /** `packaging_content_unit`: en qué unidad está ese contenido («mL», «L», «g»…). */
  contentUnit?: string | null;
}

export interface ContainerConversion {
  /** Total ya en la unidad del producto. */
  value: number;
  /** Lo que trae un envase, en la unidad del producto (para enseñar la cuenta). */
  perContainer: number;
  /** Cuántos envases eran. */
  containers: number;
}

export function convertViaContainers(
  qty: number,
  from: string | null | undefined,
  to: string | null | undefined,
  container: ContainerSpec | null | undefined,
): ContainerConversion | null {
  if (!isFinite(qty) || qty <= 0) return null;

  const f = normalizeUnit(from);
  const t = normalizeUnit(to);
  // Solo el salto que no se puede hacer de otra forma: contar → medir.
  if (f !== "units" || !t || FAMILY[t] === "count") return null;

  const content = Number(container?.content);
  if (!isFinite(content) || content <= 0) return null;

  /* Sin unidad de contenido se asume la del producto: un producto en litros con
     «contenido 5» y nada más significa 5 L por envase, que es como lo guarda el
     alta manual cuando no se toca el selector. */
  const perContainer = convertQuantity(content, container?.contentUnit || to, to);
  if (perContainer === null || perContainer <= 0) return null;

  return {
    value: round6(qty * perContainer),
    perContainer: round6(perContainer),
    containers: qty,
  };
}
